import { useEffect, useMemo, useState } from "react";
import { data, useLocation } from "react-router-dom";
import {
  fetchMyWallet,
  fetchMyCommissions,
  sendWithdrawRequest,
} from "../../../api/finance";
import dayjs from "dayjs";
import jalaliday from "jalaliday";
import "../../../styles/panel/user/Wallet.css";
import moment from "jalali-moment";
import { swalError, swalSuccess, swalWarning } from "../../../utils/swalAlert";

dayjs.extend(jalaliday);

// ---- ثابت‌ها ----
const PERSIAN_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];
const MIN_AMOUNT = 5_000_000;

// روش‌های واریز
const WITHDRAW_METHODS = [
  {
    id: "card",
    label: "کارت به کارت",
    placeholder: "شماره کارت (۱۶ رقم)",
    digits: 16,
  },
  { id: "shaba", label: "شبا (IBAN)", placeholder: "IR + ۲۴ رقم", digits: 24 },
  { id: "account", label: "شماره حساب", placeholder: "شماره حساب", digits: 15 },
];

const fmt = (n) => new Intl.NumberFormat("fa-IR").format(Number(n || 0));

// تبدیل ارقام فارسی/عربی به انگلیسی (برای رفع مشکل onChange روی اینپوت‌های عددی)
const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
function toEnglishDigits(str) {
  return str.replace(/[۰-۹٠-٩]/g, (ch) => {
    const p = PERSIAN_DIGITS.indexOf(ch);
    if (p !== -1) return String(p);
    const a = ARABIC_DIGITS.indexOf(ch);
    if (a !== -1) return String(a);
    return ch;
  });
}
function onlyDigits(str) {
  return toEnglishDigits(str).replace(/[^\d]/g, "");
}

const getJalaliYear = (date) => moment(date).format("jYYYY");
const getJalaliMonth = (date) => moment(date).format("jMM");

// ---- آیکن‌ها ----
const WalletIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8a2 2 0 012-2h11a3 3 0 013 3v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
    <path d="M16 6V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1" />
    <circle cx="16.2" cy="13" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);
const IncomeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19V9m6 10V5m6 14v-7" />
    <path d="M3 19h18" />
  </svg>
);
const WithdrawnIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);
const RangeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);
const ChevDown = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2.3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const InfoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="8" r="0.2" fill="currentColor" stroke="currentColor" />
  </svg>
);

function StatCard({ icon, label, value, tone }) {
  return (
    <div className={`wallet-stat${tone ? ` tone-${tone}` : ""}`}>
      <div className="wallet-stat-icon">{icon}</div>
      <div className="wallet-stat-body">
        <div className="wallet-stat-label">{label}</div>
        <div className="wallet-stat-value">
          {fmt(value)} <span className="wallet-currency">تومان</span>
        </div>
      </div>
    </div>
  );
}

export default function Wallet() {
  const location = useLocation();
  const basePath = location.pathname.startsWith("/marketer")
    ? "/marketer"
    : "/app";

  const [wallet, setWallet] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // فیلتر ماه/سال ("all" = همه)
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  // مودال برداشت
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState(WITHDRAW_METHODS[0].id);
  const [methodSelectOpen, setMethodSelectOpen] = useState(false);
  const [destination, setDestination] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([fetchMyWallet(), fetchMyCommissions()])
      .then(([walletRes, commissionsRes]) => {
        setWallet(walletRes.data);
        setCommissions(commissionsRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const reloadWallet = async () => {
    const res = await fetchMyWallet();
    setWallet(res.data);
  };

  // سال‌های موجود در داده‌ها (برای پر کردن دراپ‌داون سال)
  const years = useMemo(
    () =>
      [...new Set(commissions.map((i) => getJalaliYear(i.created_at)))].sort(
        (a, b) => b - a,
      ),
    [commissions],
  );

  const filtered = useMemo(() => {
    return commissions.filter((item) => {
      const y = getJalaliYear(item.created_at);
      const m = getJalaliMonth(item.created_at);

      if (year !== "all" && y !== year) return false;
      if (month !== "all" && m !== month) return false;

      // درآمد حاصل از معرفی خود فرد به خودش حساب نشود
      if (item.owner_phone === item.creator_phone) return false;

      return true;
    });
  }, [commissions, year, month]);

  const filteredIncome = filtered.reduce(
    (sum, item) => sum + Number(item.commission_amount || 0),
    0,
  );

  const totalIncome = useMemo(
    () =>
      commissions
        .filter((item) => item.owner_phone !== item.creator_phone)
        .reduce((sum, item) => sum + Number(item.commission_amount || 0), 0),
    [commissions],
  );

  const balance = Number(wallet?.balance || 0);
  const totalWithdrawn =
    wallet?.total_withdrawn != null
      ? Number(wallet.total_withdrawn)
      : Math.max(totalIncome - balance, 0);

  // ---- مودال برداشت ----
  const selectedMethod =
    WITHDRAW_METHODS.find((m) => m.id === method) ?? WITHDRAW_METHODS[0];

  const openWithdrawModal = () => {
    setAmount("");
    setMethod(WITHDRAW_METHODS[0].id);
    setMethodSelectOpen(false);
    setDestination("");
    setShowWithdrawModal(true);
  };
  const closeWithdrawModal = () => {
    if (submitting) return;
    setShowWithdrawModal(false);
  };

  const setMaxAmount = () => setAmount(String(balance));
  const numericAmount = Number(onlyDigits(amount)) || 0;

  // ثبت برداشت
  const handleWithdrawSubmit = async () => {
    if (numericAmount < MIN_AMOUNT) {
      return swalError({
        text: `حداقل مبلغ ${fmt(MIN_AMOUNT)} تومان است`,
      });
    }

    if (numericAmount > balance) {
      return swalError({
        text: "مبلغ بیشتر از موجودی کیف پول است",
      });
    }

    if (!destination.trim()) {
      return swalError({
        text: "شماره مقصد را وارد کنید",
      });
    }

    setSubmitting(true);
    try {
      await sendWithdrawRequest({
        amount: numericAmount,
        method,
        destination,
      });

      await swalSuccess({
        title: "success",
        text: "درخواست برداشت ثبت شد",
      });

      setShowWithdrawModal(false);
      setAmount("");
      setDestination("");

      await reloadWallet();
    } catch (err) {
      swalError({
        text: err?.response?.data?.detail || "مشکل در ثبت درخواست",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="wallet-page">
        <p>در حال بارگذاری کیف پول...</p>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <div className="wallet-head">
        <div>
          <h1>کیف پول</h1>
          <p>وضعیت مالی حساب خود را مشاهده و درخواست برداشت ثبت کنید.</p>
        </div>

        {/* فیلتر ماه و سال */}
        <div className="wallet-filter">
          <div className="wf-select-wrap">
            <button
              type="button"
              className="wf-select"
              aria-expanded={monthOpen}
              onClick={() => {
                setMonthOpen((v) => !v);
                setYearOpen(false);
              }}
            >
              <span>
                {month === "all"
                  ? "همه ماه‌ها"
                  : PERSIAN_MONTHS[Number(month) - 1]}
              </span>
              <ChevDown />
            </button>
            {monthOpen && (
              <div className="wf-dropdown">
                <button
                  className={`wf-option${month === "all" ? " active" : ""}`}
                  onClick={() => {
                    setMonth("all");
                    setMonthOpen(false);
                  }}
                >
                  همه ماه‌ها
                </button>
                {PERSIAN_MONTHS.map((m, i) => {
                  const value = String(i + 1).padStart(2, "0");
                  return (
                    <button
                      key={m}
                      className={`wf-option${value === month ? " active" : ""}`}
                      onClick={() => {
                        setMonth(value);
                        setMonthOpen(false);
                      }}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="wf-select-wrap">
            <button
              type="button"
              className="wf-select"
              aria-expanded={yearOpen}
              onClick={() => {
                setYearOpen((v) => !v);
                setMonthOpen(false);
              }}
            >
              <span>{year === "all" ? "همه سال‌ها" : year}</span>
              <ChevDown />
            </button>
            {yearOpen && (
              <div className="wf-dropdown">
                <button
                  className={`wf-option${year === "all" ? " active" : ""}`}
                  onClick={() => {
                    setYear("all");
                    setYearOpen(false);
                  }}
                >
                  همه سال‌ها
                </button>
                {years.map((y) => (
                  <button
                    key={y}
                    className={`wf-option${y === year ? " active" : ""}`}
                    onClick={() => {
                      setYear(y);
                      setYearOpen(false);
                    }}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* موجودی قابل برداشت */}
      <div className="wallet-balance-card">
        <div className="wbc-info">
          <div className="wbc-icon">
            <WalletIcon />
          </div>
          <div className="wbc-text">
            <div className="wbc-label">موجودی قابل برداشت</div>
            <div className="wbc-value">
              {fmt(balance)} <span>تومان</span>
            </div>
          </div>
        </div>
        <div className="wbc-action">
          <button
            type="button"
            className="wbc-btn"
            aria-disabled={balance < MIN_AMOUNT}
            onClick={() => {
              if (balance < MIN_AMOUNT) {
                swalWarning({
                  title: `حداقل مبلغ قابل برداشت ${fmt(MIN_AMOUNT)} تومان است. پس از رسیدن موجودی به این مبلغ، امکان برداشت برای شما فعال خواهد شد..`,
                });
                return;
              }
              openWithdrawModal();
            }}
          >
            درخواست برداشت
          </button>
        </div>
      </div>

      {/* آمار مالی */}
      <div className="wallet-stats-grid">
        <StatCard
          icon={<IncomeIcon />}
          label="کل درآمد"
          value={totalIncome}
          tone="income"
        />
        <StatCard
          icon={<WithdrawnIcon />}
          label="برداشت شده"
          value={totalWithdrawn}
          tone="withdrawn"
        />
        <StatCard
          icon={<RangeIcon />}
          label="درآمد در بازه انتخابی"
          value={filteredIncome}
          tone="range"
        />
      </div>

      {/* مودال درخواست برداشت */}
      {showWithdrawModal && (
        <div className="wallet-modal-overlay" onClick={closeWithdrawModal}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="wm-close"
              onClick={closeWithdrawModal}
              aria-label="بستن"
              disabled={submitting}
            >
              <CloseIcon />
            </button>

            <div className="wm-icon">
              <WalletIcon />
            </div>
            <h2>درخواست برداشت وجه</h2>
            <p className="wm-desc">
              مبلغ مورد نظر برای برداشت را وارد کنید. حداکثر مبلغ قابل برداشت{" "}
              {fmt(balance)} تومان است.
            </p>

            <label className="wm-field-label" htmlFor="withdraw-amount">
              مبلغ (تومان)
            </label>
            <div className="wm-amount-row">
              <input
                id="withdraw-amount"
                className="wm-input"
                inputMode="numeric"
                value={
                  amount
                    ? Number(onlyDigits(amount)).toLocaleString("fa-IR")
                    : ""
                }
                onChange={(e) => setAmount(onlyDigits(e.target.value))}
              />
              <button
                type="button"
                className="wm-max-btn"
                onClick={setMaxAmount}
              >
                برداشت کل موجودی
              </button>
            </div>
            {amount.length > 0 &&
              (numericAmount < MIN_AMOUNT || numericAmount > balance) && (
                <div className="wm-error">
                  مبلغ باید بین {fmt(MIN_AMOUNT)} و {fmt(balance)} تومان باشد.
                </div>
              )}

            <label className="wm-field-label" htmlFor="withdraw-method">
              روش واریز
            </label>
            <div className="wf-select-wrap wm-method-wrap">
              <button
                type="button"
                id="withdraw-method"
                className="wf-select wm-method-select"
                aria-expanded={methodSelectOpen}
                onClick={() => setMethodSelectOpen((v) => !v)}
              >
                <span>{selectedMethod.label}</span>
                <ChevDown />
              </button>
              {methodSelectOpen && (
                <div className="wf-dropdown wm-method-dropdown">
                  {WITHDRAW_METHODS.map((m) => (
                    <button
                      key={m.id}
                      className={`wf-option${m.id === method ? " active" : ""}`}
                      onClick={() => {
                        setMethod(m.id);
                        setDestination("");
                        setMethodSelectOpen(false);
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="wm-field-label" htmlFor="withdraw-destination">
              شماره مقصد
            </label>
            <input
              id="withdraw-destination"
              className="wm-input wm-destination-input"
              inputMode="numeric"
              placeholder={selectedMethod.placeholder}
              value={destination}
              onChange={(e) =>
                setDestination(
                  toEnglishDigits(e.target.value).replace(/[^\dA-Za-z]/g, ""),
                )
              }
              maxLength={selectedMethod.digits + 2}
            />

            <div className="wm-actions">
              <button
                className="wm-cancel"
                onClick={closeWithdrawModal}
                disabled={submitting}
              >
                انصراف
              </button>
              <button
                className="wm-submit"
                disabled={submitting}
                onClick={handleWithdrawSubmit}
              >
                {submitting ? "در حال ثبت..." : "ثبت درخواست"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
